import { Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TextAreaNew from "@/components/Core/TextArea/TextAreaNew";
import { routes } from "@/configs/constants/routes";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { useSubsidiaries } from "@/networking/subsidiary.hooks";
import { EmploymentContractPreviewButton } from "./EmploymentContractPreview";
import { SettingsLeaf } from "./SettingsLeafLayout";
import { primaryButtonClasses } from "./TaxonomyCrudEditor";

const AnstallningsavtalPage = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation({ expand: "recruitmentContract" });
	const { mutate: updateOrganisation, isLoading: isSaving } = useUpdateOrganisation();
	const { subsidiaries } = useSubsidiaries();

	// `null` until the user edits, so the fetched template can hydrate late.
	const [draft, setDraft] = useState<string | null>(null);
	const template = draft ?? organisation?.recruitmentContract ?? "";

	function save() {
		updateOrganisation({ recruitmentContract: template });
	}

	return (
		<SettingsLeaf
			title="Anställningsavtal"
			description={
				<>
					HTML-mall för anställningsavtal. Använd{" "}
					<a
						className="text-action underline"
						href="https://handlebarsjs.com/"
						target="_blank"
						rel="noopener noreferrer"
					>
						handlebars-syntax
					</a>{" "}
					för att ange variabler.
				</>
			}
		>
			<TextAreaNew
				height="50vh"
				value={template}
				onChangeText={setDraft}
				textareaClassName="font-mono text-xs leading-relaxed"
				resize="vertical"
			/>

			<div className="flex items-center gap-2">
				<button type="button" onClick={save} disabled={isSaving} className={primaryButtonClasses}>
					{t("SAVE")}
				</button>
				<EmploymentContractPreviewButton template={template} organisation={organisation} />
			</div>

			{subsidiaries.length > 0 && (
				<div className="flex items-start gap-2.5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
					<Info size={16} className="mt-0.5 shrink-0" />
					<p>
						Organisationen har dotterbolag. Ett dotterbolag kan ha ett eget anställningsavtal som
						ersätter den här mallen när en konsult i dotterbolaget anställs — det anges under{" "}
						<Link
							to={`${routes.SETTINGS}/subsidiaries`}
							className="font-medium underline hover:text-blue-700"
						>
							Dotterbolag
						</Link>
						.
					</p>
				</div>
			)}
		</SettingsLeaf>
	);
};

export default AnstallningsavtalPage;
